import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth, readPermissionsHint } from './AuthContext';
import { resolvePageForPath } from './pagePaths';
import { rememberMfaReturnTo } from './mfaReturnTo';
import NoAccessPage from '../components/shared/NoAccessPage';
import MaintenancePage from '../components/shared/MaintenancePage';
import { useAppDispatch } from '../store/types';
import { prefetchForPath } from '../routes/prefetch';

function FullPageSpinner() {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

export default function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, permissions, permissionsReady, user } = useAuth();
  const location = useLocation();
  const dispatch = useAppDispatch();

  // Hold the page until /api/auth/me has answered for THIS user, UNLESS the cached hint
  // confidently tells us the user has full access (super-admin OR enforcement off).
  // Without this gate, restricted users would briefly see the page + sidebar items they
  // don't have access to before the response lands and hides them — exactly the
  // flash-of-restricted-content we want to avoid. Held on `permissionsReady`, not on
  // "loading": the render between the session restoring and the request starting used to
  // mount the page once, unmount it for the spinner, and mount it again (2026-10-14).
  const hint = readPermissionsHint(user?.email);
  const hintSaysFullAccess = !!hint && (hint.isSuperAdmin || !hint.enforcementEnabled);
  const holdForPermissions = isAuthenticated && !permissionsReady && !hintSaysFullAccess;

  // While the page is held, start the data it will ask for first, so the two waits overlap
  // instead of following each other (the profile page "blinked twice" on a reload).
  useEffect(() => {
    if (holdForPermissions) prefetchForPath(location.pathname, dispatch);
  }, [holdForPermissions, location.pathname, dispatch]);

  if (isLoading) return <FullPageSpinner />;

  if (!isAuthenticated) {
    // Remember the page so sign-in (and the emailed code after it) come back here. Without it
    // a link opened while signed out landed on / once the code was entered (2026-10-09).
    rememberMfaReturnTo(location.pathname + location.search + location.hash);
    return <Navigate to="/login" replace />;
  }

  // Super-admins on revisit and any user when enforcement is off skip the
  // spinner entirely (the hint marks them as safe), so the existing fast
  // path for the common case is preserved.
  if (holdForPermissions) return <FullPageSpinner />;

  // Maintenance mode: super-admins bypass so they can turn it off. Everyone
  // else sees the splash until maintenance is flipped off.
  // Mandatory email OTP (2026-09-15): a fresh Cognito login has to pass the
  // emailed code before anything else renders. /mfa itself is outside this guard.
  // A revoked session never reaches here: AuthContext signs it out, so the
  // !isAuthenticated branch above sends it to /login.
  if (permissions && permissions.mfa.required && !permissions.mfa.verified && !location.pathname.startsWith('/mfa')) {
    rememberMfaReturnTo(location.pathname + location.search + location.hash);
    return <Navigate to="/mfa" replace />;
  }

  if (permissions && permissions.maintenanceMode && !permissions.isSuperAdmin) {
    return <MaintenancePage />;
  }

  // Role-based access check. ONLY fires when ALL of these are true:
  //   1. /api/auth/me has resolved (permissions !== null)
  //   2. Enforcement toggle is ON (default is OFF)
  //   3. Current user is not a super-admin (they bypass)
  //   4. Current route maps to a known pageId (unknown routes pass through)
  //   5. That pageId is NOT in the user's allowedPages
  if (permissions && permissions.enforcementEnabled && !permissions.isSuperAdmin) {
    const pageId = resolvePageForPath(location.pathname);
    if (pageId && !permissions.allowedPages.includes(pageId)) {
      return <NoAccessPage />;
    }
  }

  return <>{children}</>;
}
